import React, { Component } from "react";
import { Modal } from "semantic-ui-react";
import ReCAPTCHA from "react-google-recaptcha";
import { AxiosError } from "axios";
import { postRequest } from "../../../utils/httpRequest";
import { Errors, formErrors } from "../../../utils/helpers";
import { trackCourseBrochureDownload, trackNewsletterSignup } from "../../../utils/analytics";
import { fetchNextFunFact, startFunFactsAnywhere } from "../../../utils/funFacts";

// A brochure made earlier today comes back in under a second, but a new one
// takes several, and a button that just says "Preparing" for that long looks
// broken. So once a download has taken longer than BAR_AFTER, the box shows a
// bar and a fact to read instead. The bar fills quickly at first and then more
// and more slowly, so it keeps moving however long the server takes but never
// looks finished before the file has arrived: about half full after BAR_PACE,
// four-fifths after twice that, and never more than BAR_MOST.
const BAR_AFTER = 600;
const BAR_PACE = 5000;
const BAR_MOST = 96;
// Long enough to see the bar reach the end before the box moves on.
const BAR_FULL_PAUSE = 400;
// The fact moves on by itself this often, until someone uses the arrows.
const FACT_EVERY = 5000;
// As long as the longest fact (October 2026). It sits invisibly behind the
// fact on screen, so the box keeps one height whichever fact it shows, at any
// screen width.
const FACT_SIZER =
  "We value what we build: in one study, people who put together an IKEA storage box bid 63% more for it than people offered the same box ready-made. It's the IKEA effect.";

interface Props {
  open: boolean;
  onClose: () => void;
  courseName: string;
  courseSlug: string;
  // Set when someone is signed in: the newsletter goes to this address and
  // no form is needed.
  signedInEmail?: string;
}

interface FieldErrors {
  email?: string;
  first_name?: string;
}

interface State {
  wantsNewsletter: boolean;
  firstName: string;
  email: string;
  captchaToken: string;
  working: boolean;
  // The brochure is being made and the bar is showing.
  waiting: boolean;
  progress: number;
  // The facts shown while waiting, oldest first, and which one is on screen.
  facts: string[];
  factIndex: number;
  factLoading: boolean;
  // Once someone uses the arrows, the facts stay put until they move them.
  factsByHand: boolean;
  done: boolean;
  subscribed: boolean;
  errors: FieldErrors;
  problem: string;
}

const INITIAL: State = {
  wantsNewsletter: false,
  firstName: "",
  email: "",
  captchaToken: "",
  working: false,
  waiting: false,
  progress: 0,
  facts: [],
  factIndex: 0,
  factLoading: false,
  factsByHand: false,
  done: false,
  subscribed: false,
  errors: {},
  problem: "",
};

// One click to confirm the download, with the newsletter offered alongside it.
// The box starts unticked: a pre-ticked box is not consent under UK GDPR.
// When it is ticked, the sign-up has to go through before the download starts,
// so a mistyped email can be put right while the box is still open.
class BrochureDownload extends Component<Props, State> {
  state: State = INITIAL;

  unmounted = false;

  // Bumped when the box closes, so a sign-up or download still under way from
  // before cannot start a download or change a box opened afterwards.
  attempt = 0;

  waitTimer?: number;

  barTimer?: number;

  factTimer?: number;

  componentWillUnmount() {
    this.unmounted = true;
    this.stopWaiting();
  }

  stale(attempt: number) {
    return this.unmounted || attempt !== this.attempt;
  }

  brochureUrl() {
    return `/courses/${this.props.courseSlug}/brochure.pdf`;
  }

  close = () => {
    this.attempt += 1;
    this.stopWaiting();
    this.setState(INITIAL);
    this.props.onClose();
  };

  // The bar and the fact appear only if the download is still going after
  // BAR_AFTER, so a brochure that is ready at once doesn't flash them, and
  // nobody uses up a fact they never saw.
  startWaiting(attempt: number) {
    const started = Date.now();
    this.waitTimer = window.setTimeout(() => {
      if (this.stale(attempt)) return;
      this.setState({ waiting: true, progress: 0, facts: [], factIndex: 0, factsByHand: false });
      startFunFactsAnywhere();
      this.nextFact(attempt);
      this.barTimer = window.setInterval(() => {
        if (this.stale(attempt)) return;
        const progress = BAR_MOST * (1 - Math.exp(-(Date.now() - started) / BAR_PACE));
        this.setState({ progress });
      }, 200);
    }, BAR_AFTER);
  }

  stopWaiting() {
    window.clearTimeout(this.waitTimer);
    window.clearInterval(this.barTimer);
    window.clearTimeout(this.factTimer);
  }

  // Forward to a fact already seen, after going back, or else the next one in
  // the same order as the profile's "Did you know?" pop-up. If none comes, the
  // box simply shows the bar on its own.
  nextFact = (attempt: number) => {
    const { facts, factIndex } = this.state;
    if (factIndex < facts.length - 1) {
      this.setState({ factIndex: factIndex + 1 });
      return;
    }

    this.setState({ factLoading: true });
    fetchNextFunFact(facts[facts.length - 1])
      .then((fact) => {
        if (this.stale(attempt) || !this.state.waiting) return;
        const seen = fact ? [...this.state.facts, fact] : this.state.facts;
        this.setState({ facts: seen, factIndex: Math.max(seen.length - 1, 0), factLoading: false });
        this.queueFact(attempt);
      })
      .catch(() => {
        if (!this.stale(attempt)) this.setState({ factLoading: false });
      });
  };

  // Each fact gets FACT_EVERY from when it arrives, not from when it was asked
  // for.
  queueFact(attempt: number) {
    window.clearTimeout(this.factTimer);
    if (this.state.factsByHand) return;
    this.factTimer = window.setTimeout(() => {
      if (!this.stale(attempt) && this.state.waiting && !this.state.factsByHand) this.nextFact(attempt);
    }, FACT_EVERY);
  }

  stepFact = (step: 1 | -1) => {
    window.clearTimeout(this.factTimer);
    this.setState({ factsByHand: true });
    if (step === 1) this.nextFact(this.attempt);
    else this.setState({ factIndex: Math.max(this.state.factIndex - 1, 0) });
  };

  // Signed in: the account's own address, which needs no captcha. Signed out:
  // the name, email and captcha typed into the box.
  subscribe = () => {
    const { signedInEmail } = this.props;
    const { firstName, email, captchaToken } = this.state;
    const body = signedInEmail
      ? { subscription: { email: signedInEmail } }
      : { subscription: { first_name: firstName, email }, recaptcha_token: captchaToken };

    return postRequest("/api/subscriptions.json", body);
  };

  // The PDF is made on request and takes a few seconds, so it is fetched here
  // and handed over once ready, with the button saying so in the meantime.
  // "failed" when the server could not make it. When the browser cannot fetch
  // it this way at all, it is sent to the file directly: "handed-off", since
  // nothing here can tell whether that worked.
  download = async (attempt: number): Promise<"delivered" | "handed-off" | "failed"> => {
    const url = this.brochureUrl();
    let response: Response;
    try {
      response = await fetch(url, { credentials: "same-origin" });
    } catch {
      if (this.stale(attempt)) return "failed";
      window.location.href = url;
      return "handed-off";
    }
    if (!response.ok) return "failed";

    try {
      const blob = await response.blob();
      if (this.stale(attempt)) return "failed";
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${this.props.courseSlug}-42courses-brochure.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 60000);
    } catch {
      if (this.stale(attempt)) return "failed";
      window.location.href = url;
      return "handed-off";
    }
    return "delivered";
  };

  onDownload = async () => {
    const { courseSlug } = this.props;
    const { wantsNewsletter } = this.state;
    const attempt = this.attempt;
    this.setState({ working: true, errors: {}, problem: "" });

    let subscribed = this.state.subscribed;
    if (wantsNewsletter && !subscribed) {
      try {
        await this.subscribe();
        subscribed = true;
        trackNewsletterSignup({ list: "general" });
      } catch (error) {
        if (this.stale(attempt)) return;
        const data = (error as AxiosError<{ errors: Errors; captcha_error: string }>).response?.data;
        const errors = formErrors(data?.errors || []) as FieldErrors;
        let problem = "";
        if (data?.captcha_error) problem = "Please tick the box to show you're not a robot.";
        else if (!errors.first_name && !errors.email)
          problem = "We couldn't sign you up just now. Try again, or untick the box to just download.";
        this.setState({ working: false, errors, problem });
        return;
      }
    }

    if (this.stale(attempt)) return;
    this.startWaiting(attempt);
    const outcome = await this.download(attempt);
    // Closing the box already stopped this download's timers; stopping them
    // again here would stop those of a box opened since.
    if (this.stale(attempt)) return;
    this.stopWaiting();
    if (outcome === "delivered" && this.state.waiting) {
      this.setState({ progress: 100 });
      await new Promise((resolve) => setTimeout(resolve, BAR_FULL_PAUSE));
      if (this.stale(attempt)) return;
    }
    if (outcome === "handed-off") {
      // The browser is opening the file itself; nothing to confirm or count.
      this.close();
      return;
    }
    if (outcome === "failed") {
      // Signed up already, so trying again only fetches the brochure.
      this.setState({
        working: false,
        waiting: false,
        subscribed,
        problem: `${subscribed ? "You're on the list. " : ""}The brochure couldn't be made just now. Please try again in a minute.`,
      });
      return;
    }

    trackCourseBrochureDownload({ courseSlug, newsletter: subscribed });
    this.setState({ working: false, waiting: false, done: true, subscribed });
  };

  renderNewsletterFields() {
    const { signedInEmail } = this.props;
    const { firstName, email, errors } = this.state;

    if (signedInEmail) {
      return <p className="brochure-modal-note">We'll send them to {signedInEmail}.</p>;
    }

    return (
      <div className="brochure-modal-fields">
        <input
          aria-label="First name"
          placeholder="First name"
          autoComplete="given-name"
          value={firstName}
          onChange={(event) =>
            this.setState({ firstName: event.target.value, errors: { ...errors, first_name: undefined } })
          }
        />
        {errors.first_name && <p className="brochure-modal-error">First name {errors.first_name}</p>}
        <input
          aria-label="Email"
          type="email"
          placeholder="Email"
          autoComplete="email"
          value={email}
          onChange={(event) => this.setState({ email: event.target.value, errors: { ...errors, email: undefined } })}
        />
        {errors.email && <p className="brochure-modal-error">Email {errors.email}</p>}
        <ReCAPTCHA
          sitekey={process.env.RECAPTCHA_SITE_TOKEN}
          onChange={(token: string | null) => this.setState({ captchaToken: token || "", problem: "" })}
        />
      </div>
    );
  }

  renderWaiting() {
    const { progress, facts, factIndex, factLoading, factsByHand } = this.state;
    const fact = facts[factIndex];

    return (
      <div className="brochure-modal-wait">
        <p className="brochure-modal-about" role="status">
          <strong>Putting your brochure together.</strong> It takes a few seconds and will download by
          itself, so keep this box open.
        </p>
        <div
          className="brochure-modal-bar"
          role="progressbar"
          aria-label="Putting your brochure together"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
        >
          <span style={{ width: `${progress}%` }} />
        </div>
        {(fact || factLoading) && (
          <div className="brochure-modal-fact">
            {/* Read out only when someone moves the facts on themselves, not
                every few seconds while they wait. */}
            <div className="brochure-modal-fact-text" aria-live={factsByHand ? "polite" : "off"}>
              <p key={factIndex}>{fact}</p>
              <p className="brochure-modal-fact-sizer" aria-hidden="true">
                {FACT_SIZER}
              </p>
            </div>
            <div className="brochure-modal-fact-arrows">
              <button
                type="button"
                aria-label="Previous fact"
                onClick={() => this.stepFact(-1)}
                disabled={factLoading || factIndex === 0}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M15 5l-7 7 7 7" />
                </svg>
              </button>
              <button
                type="button"
                aria-label="Next fact"
                onClick={() => this.stepFact(1)}
                disabled={factLoading}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderDone() {
    const { signedInEmail } = this.props;
    const { subscribed, email } = this.state;

    return (
      <div className="brochure-modal-done" role="status">
        <p className="brochure-modal-about">
          Your brochure is downloading. If nothing happens,{" "}
          <a href={this.brochureUrl()}>open it here</a>.
        </p>
        {subscribed && (
          <p className="brochure-modal-about">
            You're on the list too: Weekend Reads and Weekday Wisdom will come to{" "}
            {signedInEmail || email}.
          </p>
        )}
        <div className="brochure-modal-actions">
          <button type="button" className="brochure-modal-download" onClick={this.close}>
            Done
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { open, courseName } = this.props;
    const { wantsNewsletter, working, waiting, done, problem } = this.state;

    return (
      <Modal className="brochure-modal" size="tiny" open={open} onClose={this.close} closeIcon>
        <div className="brochure-modal-body">
          <div className="brochure-modal-head">
            <svg className="brochure-modal-icon" viewBox="0 0 40 48" aria-hidden="true">
              <path d="M4 2h22l10 10v32a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
              <path d="M26 2v10h10" />
              <path d="M10 22h20M10 29h20M10 36h13" />
            </svg>
            <h3>{courseName}</h3>
          </div>

          {done && this.renderDone()}
          {!done && waiting && this.renderWaiting()}
          {!done && !waiting && (
            <>
              <p className="brochure-modal-about">
                The whole course in a marvellous nutshell.
              </p>

              <label className="brochure-modal-tick">
                <input
                  type="checkbox"
                  checked={wantsNewsletter}
                  onChange={(event) => this.setState({ wantsNewsletter: event.target.checked, problem: "" })}
                />
                <span>
                  <strong>Also for free send me Weekend Reads</strong> and Weekday Wisdom
                  education emails on Fridays and Tuesdays. Unsubscribe any time.
                </span>
              </label>

              {wantsNewsletter && this.renderNewsletterFields()}

              {problem && (
                <p className="brochure-modal-error" role="alert">
                  {problem}
                </p>
              )}

              <div className="brochure-modal-actions">
                <button
                  type="button"
                  className="brochure-modal-download"
                  onClick={this.onDownload}
                  disabled={working}
                >
                  {working ? "Preparing your brochure…" : "Download the brochure"}
                </button>
                <button type="button" className="brochure-modal-cancel" onClick={this.close}>
                  Not now
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    );
  }
}

export default BrochureDownload;
